<template>
  <p>
    La física de la atmósfera utiliza un conjunto de instrumentos para
    monitorear las diferentes variables de los procesos que ocurren en la
    atmósfera. Esta instrumentación científica y su funcionamiento a largo plazo
    es clave para la generación de bases de datos confiables.
  </p>
  <p>
    En ese sentido el IGP desde el año 2020 ha implementado la estación
    meteorológica automática (EMA), la cual registra los datos cada 1 minutos
    para ser el centro de las observaciones de las labores de física de la
    atmosfera en el Observatorio de Huancayo.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/ema.webp"
    alt="Estación Meteorológica Automática"
  />
  <p>
    La EMA cuenta con los siguientes sensores:
  </p>

  <List>
    <ol>
      <li>
        Piranómetro y pirgeómetro (modelo CGR3 de Kipp &amp; Zonen, Holanda).
      </li>
      <li>Termohigrometro (modelo HMP45 de Vaisala, Finlandia).</li>
      <li>Pluviometro (modelo CS700 de Campbell Sci, EE.UU.)</li>
      <li>Barómetro (modelo 278 de Setra, EE.UU.)</li>
      <li>Anemómetro (modelo 05103V de Young, EE.UU.)</li>
      <li>Datalogger (modelo CR1000 de Campbell Scientific, EE.UU.)</li>
    </ol>
  </List>
  <p>
    <b> Piranómetro </b>
  </p>
  <p>
    El piranómetro es considerado como el instrumento actinométrico universal
    por que es utilizado para medir todos los flujos radiativos de onda corta.
    Este sensor está formado por una pila termoeléctrica contenida en una
    cápsula protegida por dos semiesferas de cristal, las cuales limitan el
    rango de respuesta del sensor a la radiación de onda corta. El piranómetro
    empleado en la estación de medición de radiación solar es de la marca Kipp
    &amp; Zonen, modelo CMP11 de la marca Kipp &amp; Zonen y según la
    clasificación ISO es de Class A (estación meteorológica{andar secondario).
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/piranometro.webp"
    alt="Piranómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Rango espectral (total)</td>
        <td>285 a 2800 nm</td>
      </tr>
      <tr>
        <td>Sensibilidad</td>
        <td>7 a 14 µV/W/m²</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>5 s</td>
      </tr>
      <tr>
        <td>Offset cero A</td>
        <td>&lt; 7 W/m²</td>
      </tr>
      <tr>
        <td>Offset cero B</td>
        <td>&lt; 2 W/m²</td>
      </tr>
      <tr>
        <td>Error direccional (hasta 80 ° a 1000 W/m²)</td>
        <td>&lt; 10 W/m²</td>
      </tr>
      <tr>
        <td>Sensibilidad de dependencia a la temperatura</td>
        <td>&lt; 1 % (-10 °C a +40 °C)</td>
      </tr>
      <tr>
        <td>Rango de temperatura de operación</td>
        <td>-40 °C a +80 °C</td>
      </tr>
      <tr>
        <td>Irradiancia solar máxima</td>
        <td>4000 W/m²</td>
      </tr>
      <tr>
        <td>Campo de visión</td>
        <td>180 °</td>
      </tr>
      <tr>
        <td>Referencia adicional</td>
        <td>
          <a href="https://www.campbellsci.es/cmp11" target="_blank"
            >https://www.campbellsci.es/cmp11</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>
  <p>
    <b>Pirgeómetro</b>
  </p>
  <p>
    Este instrumento está diseñado para la medición de la irradiancia de onda
    larga, por lo que su rango de medición abarca desde los 4500 nm hasta los
    42000 nm. La cúpula con forma de menisco garantiza que exista un error de
    respuesta direccional mínimo con un campo de visión de 150°. El modelo
    empleado en la estación es el CGR3 de la marca Kipp &amp; Zonen y se utiliza
    para la medición de la irradiancia infrarroja de la bóveda celeste.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/pirgeometro.webp"
    alt="Pirgeómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Rango espectral (total)</td>
        <td>4500 a 42000 nm</td>
      </tr>
      <tr>
        <td>Sensibilidad</td>
        <td>5 a 7 µV/W/m²</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>18 s</td>
      </tr>
      <tr>
        <td>Offset del calentamiento de la ventana</td>
        <td>&lt; 15 W/m²</td>
      </tr>
      <tr>
        <td>Offset cero B</td>
        <td>&lt; 2 W/m²</td>
      </tr>
      <tr>
        <td>
          Sensibilidad de dependencia a la temperatura (-10 °C a +40 °C)
        </td>
        <td>&lt; 5 %</td>
      </tr>
      <tr>
        <td>Rango de temperatura de operación</td>
        <td>-40 a +80 °C</td>
      </tr>
      <tr>
        <td>Límites de irradiancia neta</td>
        <td>-250 a + 250 W/m²</td>
      </tr>
      <tr>
        <td>Campo de visión</td>
        <td>150 °</td>
      </tr>

      <tr>
        <td>No-linealidad</td>
        <td>&lt; 1 %</td>
      </tr>
      <tr>
        <td>Referencia adicional</td>
        <td>
          <a
            href="https://www.otthydromet.com/en/p-kippzonen-cgr3-pyrgeometer/0359920"
            target="_blank"
            >https://www.otthydromet.com/en/p-kippzonen-cgr3-pyrgeometer/0359920</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>
  <p>
    <b>Termohigrómetro</b>
  </p>
  <p>
    El sensor HMP45A están diseñado para la medición de humedad relativa y
    temperatura del aire. La medición de la humedad se basa en el sensor
    capacitivo de polímero de película delgada HUMICAP180. La medición se basa
    en sensores resistivos de platino (Pt 100 y Pt 1000).
  </p>
  <p>
    Tanto los sensores de humedad como de temperatura están ubicados en la punta
    de la sonda y en versión estándar protegido por un filtro de membrana. El
    HMP45A tiene una salida de humedad similar, pero la salida de temperatura
    está activa en HMP45A (salida de tensión 0-1V) y pasiva en HMP45D (salida
    resistiva Parte 100).
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/termohigrometro.webp"
    alt="Termohigrómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td colspan="2"><b>Temperatura ambiental</b></td>
      </tr>
      <tr>
        <td>Tipo de sensor</td>
        <td>Resistencia de platino 1000 ohm</td>
      </tr>
      <tr>
        <td>Rango de operación</td>
        <td>-39.2 a +60.0 °C</td>
      </tr>
      <tr>
        <td>Rango de salida</td>
        <td>0.008 a 1 voltio</td>
      </tr>
      <tr>
        <td>Precisión a 0°C</td>
        <td>±.0.3</td>
      </tr>
      <tr>
        <td>Precisión a 20°C</td>
        <td>±.0.2</td>
      </tr>
      <tr>
        <td colspan="2"><b>Humedad relativa</b></td>
      </tr>
      <tr>
        <td>Tipo de sensor</td>
        <td>H-Chip HUMICAP</td>
      </tr>
      <tr>
        <td>Rango de medición</td>
        <td>0.8% a 100% de humedad</td>
      </tr>
      <tr>
        <td>Rango de salida</td>
        <td>0.008 a 1 Vdc</td>
      </tr>
      <tr>
        <td>Precisión a 20°C (contra referencia en fábrica)</td>
        <td>1 % HR</td>
      </tr>
      <tr>
        <td>Dependencia de temperatura</td>
        <td>±0.05% RH/°C</td>
      </tr>
      <tr>
        <td>Estabilidad</td>
        <td>Mejor que 1% RH por año</td>
      </tr>
      <tr>
        <td>Tiempo de respuesta (a 20°C)</td>
        <td>15 s con filtro membrana</td>
      </tr>
      <tr>
        <td>Refererencia adicional</td>
        <td>
          <a href="https://www.campbellsci.es/hmp45c-l" target="_blank"
            >https://www.campbellsci.es/hmp45c-l</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>
  <p>
    <b>Pluviómetro </b>
  </p>
  <p>
    Es un pluviómetro de tipo de cubeta basculante de gama alta. Cuenta con un
    orificio colector de 20 cm y una base de aluminio fundido de alta
    resistencia. Mide la precipitación en incrementos de 2.54 mm. El CS700,
    fabricado por HS Hyquest Solutions, es ideal para lugares donde pueden
    ocurrir lluvias intensas.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/pluviometro.webp"
    alt="Pluviómetro"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td rowspan="2">Incertidumbre de medición</td>
        <td>
          ±2% @ &lt; 250 mm/h
        </td>
      </tr>
      <tr>
        <td>
          ±3% @ 250 a 500 mm/h
        </td>
      </tr>
      <tr>
        <td>Rango de medición</td>
        <td>0 a 700 mm/h</td>
      </tr>
      <tr>
        <td>Rango de temperatura de operación</td>
        <td>0° a 70°C</td>
      </tr>
      <tr>
        <td>Rango de humedad de operación</td>
        <td>0 a 100%</td>
      </tr>
      <tr>
        <td>Tamaño de orificio colector</td>
        <td>20 cm</td>
      </tr>
      <tr>
        <td>Referencia adicional</td>
        <td>
          <a href="https://www.campbellsci.es/cs700" target="_blank"
            >https://www.campbellsci.es/cs700</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>

  <p>
    <b>Barómetro</b>
  </p>
  <p>
    El CS100 mide la presión barométrica en el rango de 600 a 1100 mb. Este
    rango equivale desde debajo del nivel del mar (como en una mina) hasta
    3657.6 m sobre el nivel del mar.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/barometro.webp"
    alt="Barómetro (Setra)"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td rowspan="2">Precisión</td>
        <td>
          ±1.0 hPa (@ +20.0 °C)
        </td>
      </tr>
      <tr>
        <td>±1.5 hPa (@ 0°C a 40°C)</td>
      </tr>
      <tr>
        <td>Rango de medición</td>
        <td>
          600 a 1 100 hPa
        </td>
      </tr>
      <tr>
        <td>Estabilidad</td>
        <td>
          ±0.1 hPa por año
        </td>
      </tr>
      <tr>
        <td>Tiempo de respuesta</td>
        <td>
          &lt; 100 ms
        </td>
      </tr>
      <tr>
        <td>Resolución</td>
        <td>
          ±0.01 hPa
        </td>
      </tr>
      <tr>
        <td>Referencia adicional</td>
        <td>
          <a href="https://www.campbellsci.es/cs100" target="_blank"
            >https://www.campbellsci.es/cs100</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>

  <!-- Instrumento -->
  <p>
    <b>Anemómetro </b>
  </p>
  <p>
    El sensor de velocidad del viento (rapidez y dirección) está compuesta por
    una hélice helicoidal de cuatro palas y una veleta. La rotación de la hélice
    produce una señal de voltaje de onda sinusoidal de CA, mientras que el
    ángulo de la paleta se detecta mediante un potenciómetro de precisión.
    Construido de plástico estabilizado a los rayos UV con accesorios de acero
    inoxidable y aluminio anodizado, el sensor se monta en una tubería estándar
    de 1 pulgada, registrando valores de 0-5 VCC.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/anemometro.webp"
    alt="Anemómetro (Young, modelo 01513V)"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td rowspan="2">Precisión</td>
        <td>
          Velocidad del viento: ±0.3 m/s o 1% de lectura
        </td>
      </tr>
      <tr>
        <td>Dirección: ±3°</td>
      </tr>
      <tr>
        <td rowspan="2">Rango de medición</td>
        <td>
          Velocidad del viento: 0 – 100 m/s
        </td>
      </tr>
      <tr>
        <td>Dirección: 360° mecánico</td>
      </tr>
      <tr>
        <td>Requerimiento de energía</td>
        <td>
          8 a 30 Vdc (5 mA @ 12 Vdc)
        </td>
      </tr>
      <tr>
        <td>Rango de temperatura de operación</td>
        <td>
          -50 a +50 °C
        </td>
      </tr>
      <tr>
        <td>Señal de salida</td>
        <td>
          0 – 5 Vdc
        </td>
      </tr>
    </tbody>
  </TableUi>

  <!-- Instrumento -->
  <p>
    <b>Termómetro de suelo </b>
  </p>
  <p>
    El sensor 107 consiste en un termistor encapsulado dentro de una vaina de
    acero inoxidable rellenada con resina. Esta se ubica a nivel de la
    superficie del suelo y dentro de un protector o caseta meteorológica, los
    cuales permiten la libre circulación del aire, y protegen de las ráfagas de
    viento, la lluvia y de la radiación solar.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/termometro-suelo.webp"
    alt="Termómetro de suelo (Campbell Scientific, modelo 107-L)"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Tipo de sensor</td>
        <td>
          Termistor 100K6A11A Betatherm
        </td>
      </tr>
      <tr>
        <td>Precisión</td>
        <td>
          ±0.2°C (rango del 0° a 50°C)
        </td>
      </tr>
      <tr>
        <td>Rango de temperatura</td>
        <td>
          -35 a +50°C
        </td>
      </tr>
      <tr>
        <td>Referencia adicional</td>
        <td>
          <a href="https://www.campbellsci.es/107" target="_blank"
            >https://www.campbellsci.es/107</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>

  <!-- Instrumento -->
  <p>
    <b>Radiómetro infrarrojo </b>
  </p>
  <p>
    Este sensor mide la temperatura de una superficie basado en la radiación
    infrarroja que emite en la banda de la ventana atmosférica. Tiene un campo
    de visión de medio ángulo de 22 grados. Se utiliza para medir la temperatura
    de la superficie del suelo, la cual puede estar cubierta de vegetación, por
    ejemplo pasto.
  </p>
  <img
    class="thumbnail instrument"
    src="@/assets/images/microfisica/instrumentos/radiometro-infrarrojo.webp"
    alt="Radiómetro infrarrojo"
  />
  <TableUi>
    <thead>
      <tr>
        <th colspan="2">Especificaciones</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Tipo de sensor</td>
        <td>
          Termopila and termistor
        </td>
      </tr>
      <tr>
        <td rowspan="2">Precisión</td>
        <td>
          ±0.2°C (-10° a +65°C)
        </td>
      </tr>
      <tr>
        <td>±0.5°C (-40° a +70°C)</td>
      </tr>
      <tr>
        <td>Rango de temperatura</td>
        <td>
          -55 a +80°C
        </td>
      </tr>
      <tr>
        <td>Rango de longitud de onda</td>
        <td>
          8 a 14 μm (corresponde a la ventana atmosférica)
        </td>
      </tr>
      <tr>
        <td>Referencia adicional</td>
        <td>
          <a href="https://www.campbellsci.es/si-111ss" target="_blank"
            >https://www.campbellsci.es/si-111ss</a
          >
        </td>
      </tr>
    </tbody>
  </TableUi>
</template>

<script>
import TableUi from "@/components/ui/objects/TableUi.vue";
export default {
  components: { TableUi },
};
</script>
